'use client'

import { getLog } from '@eigtech/ui-shared-logging'
import { useControllableStateWithContext } from '@eigtech/ui/hooks/use-controllable-state'
import { heicTo, isHeic } from 'heic-to'
import type React from 'react'
import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type InputHTMLAttributes,
} from 'react'
import { FileEntry } from '../types/file-entry'

const log = getLog()

type FileUploadChangeContext =
  | {
      action: 'add'
      files: FileEntry[]
    }
  | {
      action: 'clear'
    }
  | {
      action: 'remove'
      ids: string[]
    }
  | {
      action: 'update'
      file: FileEntry
    }

export type FileUploadOptions = {
  convertHeic?: boolean | { type: 'jpeg' | 'png'; quality?: number }
  files?: FileEntry[]
  maxFiles?: number // Only used when multiple is true, defaults to Infinity
  maxSize?: number // in bytes
  accept?: string
  multiple?: boolean // Defaults to false
  disabled?: boolean
  onFilesChange?: (files: FileEntry[], context: FileUploadChangeContext) => void // Callback when files change
  onError?: (errors: string[]) => void
}

export type FileUploadState = {
  files: FileEntry[]
  isDragging: boolean
  errors: string[]
}

export type FileUploadActions = {
  addFiles: (files: FileList | File[]) => void
  removeFile: (id: string, forceRemove?: boolean) => void
  clearFiles: () => void
  clearErrors: () => void
  handleDragEnter: (e: DragEvent<HTMLElement>) => void
  handleDragLeave: (e: DragEvent<HTMLElement>) => void
  handleDragOver: (e: DragEvent<HTMLElement>) => void
  handleDrop: (e: DragEvent<HTMLElement>) => void
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void
  openFileDialog: () => void
  getInputProps: (
    props?: InputHTMLAttributes<HTMLInputElement>
  ) => InputHTMLAttributes<HTMLInputElement> & {
    ref: React.Ref<HTMLInputElement>
  }
}

export const useFileUpload = (
  options: FileUploadOptions = {}
): [FileUploadState, FileUploadActions] => {
  const {
    convertHeic = true,
    files = [],
    maxFiles = Number.POSITIVE_INFINITY,
    maxSize = Number.POSITIVE_INFINITY,
    accept = '*',
    multiple = false,
    disabled,
    onFilesChange,
    onError,
  } = options

  const [value, setValue] = useControllableStateWithContext({
    value: files,
    defaultValue: [],
    onChange: onFilesChange,
  })

  // Separate ref holds the actual File objects — cleared as soon as each upload starts
  const fileRefs = useRef<Map<string, File>>(new Map())
  const thumbnailRefs = useRef<Map<string, string | null>>(new Map())

  const [errors, setErrors] = useState<string[]>([])
  const [isDragging, setIsDragging] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxSize) {
        return `File "${file.name}" exceeds the maximum size of ${formatBytes(maxSize)}.`
      }

      if (accept !== '*') {
        const acceptedTypes = accept.split(',').map((type) => type.trim())
        const fileType = file.type
        const fileExtension = `.${file.name.split('.').pop()}`

        const isAccepted = acceptedTypes.some((type) => {
          if (type.startsWith('.')) {
            return fileExtension.toLowerCase() === type.toLowerCase()
          }
          if (type.endsWith('/*')) {
            const baseType = type.split('/')[0]
            return fileType.startsWith(`${baseType}/`)
          }
          return fileType === type
        })

        if (!isAccepted) {
          return `File "${file.name}" is not an accepted file type.`
        }
      }

      return null
    },
    [accept, maxSize]
  )

  const clearFiles = useCallback(() => {
    if (disabled) return

    if (inputRef.current) {
      inputRef.current.value = ''
    }

    setErrors([])
    setValue([], { action: 'clear' })

    fileRefs.current.clear()
    thumbnailRefs.current.clear()
  }, [disabled, setValue])

  const addFiles = useCallback(
    async (newFiles: FileList | File[]) => {
      if (disabled) return
      if (!newFiles || newFiles.length === 0) return

      const newFilesArray = Array.from(newFiles)
      const errors: string[] = []

      // Clear existing errors when new files are uploaded
      setErrors([])

      // In single file mode, clear existing files first
      if (!multiple) {
        clearFiles()
      }

      // Check if adding these files would exceed maxFiles (only in multiple mode)
      if (
        multiple &&
        maxFiles !== Number.POSITIVE_INFINITY &&
        value.length + newFilesArray.length > maxFiles
      ) {
        errors.push(`You can only upload a maximum of ${maxFiles} files.`)
        onError?.(errors)
        setErrors(errors)
        return
      }

      const validFiles: FileEntry[] = []

      for (const file of newFilesArray) {
        // Only check for duplicates if multiple files are allowed
        if (multiple) {
          const isDuplicate = value.some((existingFileWithPreview) => {
            const existingFile = existingFileWithPreview.getFile()
            return existingFile?.name === file.name && existingFile?.size === file.size
          })

          // Skip duplicate files silently
          if (isDuplicate) {
            continue
          }
        }

        // Check file size
        if (file.size > maxSize) {
          errors.push(
            multiple
              ? `Some files exceed the maximum size of ${formatBytes(maxSize)}.`
              : `File exceeds the maximum size of ${formatBytes(maxSize)}.`
          )
          continue
        }

        const error = validateFile(file)
        if (error) {
          errors.push(error)
        } else {
          const id = generateId()

          const getThumbnail = async () => {
            if (thumbnailRefs.current.has(id)) {
              return thumbnailRefs.current.get(id) ?? null
            }

            if (!fileRefs.current.has(id)) {
              return null
            }

            const thumbnail = await generateThumbnail(fileRefs.current.get(id) ?? null)
            thumbnailRefs.current.set(id, thumbnail)

            return thumbnail ?? null
          }

          fileRefs.current.set(id, file)

          const isImageFile = isImage(file)
          const willConvertHeicFile = convertHeic && isImageFile && (await isHeic(file))

          const type =
            !convertHeic || !isImageFile
              ? file.type
              : convertHeic === true
                ? 'image/jpeg'
                : (`image/${convertHeic.type}` as const)

          const fileNameWithoutExtension = file.name.split('.').slice(0, -1).join('.')

          const name = willConvertHeicFile
            ? `${fileNameWithoutExtension}.${type.split('/').pop()}`
            : file.name

          log.info('isHeicFile', { name: file.name, isHeicFile: willConvertHeicFile, convertHeic })

          const fileEntry: FileEntry = {
            getFile: () => fileRefs.current.get(id) ?? null,
            revokeFile: () => {
              fileRefs.current.delete(id)
            },
            id,
            name,
            size: file.size,
            type,
            isImage: isImage(file),
            isVideo: isVideo(file),
            getThumbnail,
            status: willConvertHeicFile ? 'converting' : 'pending',
            updateFile(updatedFile: FileEntry) {
              if (!fileRefs.current.has(id)) {
                log.info('file not found in fileRefs, skipping updateFile', { id })
                return
              }

              setValue(
                (prev) => {
                  const newFiles = prev.map((file) => {
                    if (file.id === id) {
                      return updatedFile
                    }
                    return file
                  })
                  return newFiles
                },
                { action: 'update', file: updatedFile }
              )
            },
          }

          if (willConvertHeicFile && convertHeic) {
            heicTo({
              blob: file,
              type: type as `image/${string}`,
              quality: convertHeic === true ? 0.9 : convertHeic.quality,
            })
              .then((blob) => {
                if (!fileRefs.current.has(id)) {
                  log.info('file not found in fileRefs, skipping conversion', { id })
                  return
                }
                const newFile = new File([blob], name, { type })
                fileRefs.current.set(id, newFile)
                fileEntry.updateFile({
                  ...fileEntry,
                  size: newFile.size,
                  status: 'pending',
                })
              })
              .catch((error) => {
                if (!fileRefs.current.has(id)) {
                  log.info('file not found in fileRefs, skipping adding error', { id })
                  return
                }

                log.error('error converting heic file to', { name, error })
                fileEntry.updateFile({
                  ...fileEntry,
                  status: 'error',
                })

                setErrors((prev) => {
                  const errors = [...prev, 'Failed to convert HEIC file to JPEG or PNG.']
                  onError?.(errors)
                  return errors
                })
              })
          }

          validFiles.push(fileEntry)
        }
      }

      // Only update state if we have valid files to add
      if (validFiles.length > 0) {
        setErrors(errors)

        setValue(
          (prev) => {
            const newFiles = !multiple ? validFiles : [...prev, ...validFiles]
            return newFiles
          },
          { action: 'add', files: validFiles }
        )
      } else if (errors.length > 0) {
        onError?.(errors)
        setErrors(errors)
      }

      // Reset input value after handling files
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    },
    [
      disabled,
      multiple,
      maxFiles,
      value,
      clearFiles,
      onError,
      maxSize,
      validateFile,
      convertHeic,
      setValue,
    ]
  )

  const removeFile = useCallback(
    (id: string, forceRemove?: boolean) => {
      if (disabled && !forceRemove) {
        return
      }

      setErrors([])
      setValue(
        (prev) => {
          const fileToRemove = prev.find((file) => file.id === id)
          log.info('fileToRemove', { fileToRemove })
          fileToRemove?.revokeFile()
          fileRefs.current.delete(id)
          thumbnailRefs.current.delete(id)

          const newFiles = prev.filter((file) => file.id !== id)

          return newFiles
        },
        { action: 'remove', ids: [id] }
      )
    },
    [disabled, setValue]
  )

  const clearErrors = useCallback(() => {
    setErrors([])
  }, [])

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (disabled) return

      setIsDragging(true)
    },
    [disabled]
  )

  const handleDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return
    }

    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (disabled) return

      setIsDragging(false)

      // Don't process files if the input is disabled
      if (inputRef.current?.disabled) {
        return
      }

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        // In single file mode, only use the first file
        if (!multiple) {
          const file = e.dataTransfer.files[0]
          if (!file) return
          addFiles([file])
        } else {
          addFiles(e.dataTransfer.files)
        }
      }
    },
    [addFiles, disabled, multiple]
  )

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return

      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files)
      }
    },
    [addFiles, disabled]
  )

  const openFileDialog = useCallback(() => {
    if (disabled) return

    if (inputRef.current) {
      inputRef.current.click()
    }
  }, [disabled])

  const getInputProps = useCallback(
    (props: InputHTMLAttributes<HTMLInputElement> = {}) => {
      return {
        ...props,
        type: 'file' as const,
        onChange: handleFileChange,
        accept: props.accept || accept,
        multiple: props.multiple !== undefined ? props.multiple : multiple,
        ref: inputRef,
      }
    },
    [handleFileChange, accept, multiple]
  )

  return [
    { files: value, errors, isDragging },
    {
      addFiles,
      removeFile,
      clearFiles,
      clearErrors,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      handleFileChange,
      openFileDialog,
      getInputProps,
    },
  ]
}

// Helper function to format bytes to human-readable format
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Number.parseFloat((bytes / k ** i).toFixed(dm)) + (sizes[i] ?? 'Unknown')
}

const IMAGE_MIME_RE = /^image\//
const THUMBNAIL_MAX_PX = 400
const THUMBNAIL_QUALITY = 0.8

const isImage = (file: FileEntry | File): boolean => {
  return IMAGE_MIME_RE.test(file.type)
}

const VIDEO_MIME_RE = /^video\//
const isVideo = (file: FileEntry | File): boolean => {
  return VIDEO_MIME_RE.test(file.type)
}

/**
 * Generates a small thumbnail dataURL from a File without keeping the object
 * URL or the original File reference alive beyond this call.
 */
function generateThumbnail(file: File | null): Promise<string | null> {
  if (!file) return Promise.resolve(null)

  if (!isImage(file)) return Promise.resolve(null)

  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()

    img.onload = () => {
      // Revoke immediately — we only needed it to decode the image
      URL.revokeObjectURL(objectUrl)

      const scale = Math.min(1, THUMBNAIL_MAX_PX / Math.max(img.width, img.height))
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)

      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        return resolve(null)
      }

      ctx.drawImage(img, 0, 0, w, h)

      const dataUrl = canvas.toDataURL('image/jpeg', THUMBNAIL_QUALITY)

      // Explicitly release the canvas backing buffer
      ctx.clearRect(0, 0, w, h)
      canvas.width = 0
      canvas.height = 0

      resolve(dataUrl)
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(null)
    }

    img.src = objectUrl
  })
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}
